/**
 * QapiDashboardPage - QAPI quality assurance dashboard
 *
 * Provides QAPI (Quality Assurance Performance Improvement) dashboard for admin users.
 * Contains T3 scope-gated data for quality metrics and compliance monitoring.
 *
 * Route: /admin/qapi-dashboard
 */

import { Container, Title, Group, Badge, Text, ThemeIcon, Alert, Card, Stack, SimpleGrid, Progress, RingProgress } from '@mantine/core';
import { IconShieldCheck, IconAlertTriangle } from '@tabler/icons-react';

import { useAuth } from '@/auth/AuthContext';

/**
 * QAPI Dashboard - Quality Assurance Performance Improvement
 *
 * Note: This is a placeholder until the full QAPI app is integrated.
 * T3 data access requires RequireScope("qapi:view") on the backend.
 */
export function QapiDashboardPage() {
  const { tenant } = useAuth();

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="green">
            <IconShieldCheck size={20} />
          </ThemeIcon>
          <div>
            <Title order={3}>QAPI Dashboard</Title>
            <Text size="sm" c="dimmed">
              Quality Assurance Performance Improvement monitoring
            </Text>
          </div>
        </Group>
        {tenant && (
          <Badge variant="light" color="gray">
            {tenant.name}
          </Badge>
        )}
      </Group>

      <Alert icon={<IconAlertTriangle size={16} />} title="T3 Protected Data" color="yellow" mb="lg">
        This dashboard contains T3 scope-gated quality metrics. Access requires qapi:view scope.
      </Alert>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" mb="xl">
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text size="sm" c="dimmed" mb="xs">Compliance Score</Text>
          <Group justify="center">
            <RingProgress
              size={80}
              roundCaps
              thickness={8}
              sections={[{ value: 94, color: 'green' }]}
              label={<Text ta="center" fw={700}>94%</Text>}
            />
          </Group>
        </Card>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text size="sm" c="dimmed" mb="xs">Task Completion</Text>
          <Group justify="center">
            <RingProgress
              size={80}
              roundCaps
              thickness={8}
              sections={[{ value: 87, color: 'blue' }]}
              label={<Text ta="center" fw={700}>87%</Text>}
            />
          </Group>
        </Card>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text size="sm" c="dimmed" mb="xs">Incident Rate</Text>
          <Group justify="center">
            <RingProgress
              size={80}
              roundCaps
              thickness={8}
              sections={[{ value: 3, color: 'red' }]}
              label={<Text ta="center" fw={700}>3%</Text>}
            />
          </Group>
        </Card>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text size="sm" c="dimmed" mb="xs">Training Current</Text>
          <Group justify="center">
            <RingProgress
              size={80}
              roundCaps
              thickness={8}
              sections={[{ value: 98, color: 'teal' }]}
              label={<Text ta="center" fw={700}>98%</Text>}
            />
          </Group>
        </Card>
      </SimpleGrid>

      <Stack gap="md">
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text fw={500} mb="md">Monthly Performance Trend</Text>
          <Stack gap="sm">
            <div>
              <Group justify="space-between" mb={5}>
                <Text size="sm">Care Documentation</Text>
                <Text size="sm" c="dimmed">92%</Text>
              </Group>
              <Progress value={92} color="blue" />
            </div>
            <div>
              <Group justify="space-between" mb={5}>
                <Text size="sm">Medication Administration</Text>
                <Text size="sm" c="dimmed">96%</Text>
              </Group>
              <Progress value={96} color="green" />
            </div>
            <div>
              <Group justify="space-between" mb={5}>
                <Text size="sm">Incident Reporting</Text>
                <Text size="sm" c="dimmed">100%</Text>
              </Group>
              <Progress value={100} color="teal" />
            </div>
            <div>
              <Group justify="space-between" mb={5}>
                <Text size="sm">Care Plan Compliance</Text>
                <Text size="sm" c="dimmed">89%</Text>
              </Group>
              <Progress value={89} color="yellow" />
            </div>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}

export default QapiDashboardPage;
